import base64url from 'base64url';
import axios from 'axios';
import qs from 'qs';
import crypto from 'crypto-browserify';
import jwtDecode from 'jwt-decode';
import { checkUser, clearUser, updateUser } from './redux/login/loginActions';
import { BASE_PATH, CLIENT_ID, resolveOauthPath } from './basePath';
import {
  ACR_VALUES,
  AUTHORIZE_ENDPOINT,
  CHECK_NONCE,
  REVOKE_ENDPOINT,
  TOKEN_ENDPOINT,
} from './resourceBasePath';
import { addErrorMessage } from './redux/messages/messageActions';
import { history } from './history';
import { generateNonce, isValidNonce } from './nonceUtils';

const LOCAL_STORAGE_KEY = 'HAZEL_OAUTH';
const LOCAL_STORAGE_REFRESH_KEY = 'HAZEL_OAUTH_REFRESH';
const pollInterval = 1000;
const pollIntervalMultiplier = 240;
const loginTimeout = 240000;
const scope = 'openid profile email groups';

const maxTime = pollIntervalMultiplier * pollInterval;


export const getStored = () => localStorage.getItem(LOCAL_STORAGE_KEY) || '';

const decodeUser = (stored) => {
  const tokenData = JSON.parse(base64url.decode(stored));
  const { idToken, exp } = tokenData;
  const jwtPayload = jwtDecode(idToken);
  const { sub: userName, name: displayName } = jwtPayload;
  const authorities = [];
  return {
    idToken,
    exp,
    user: {
      userName,
      displayName,
      authorities,
    },
  };
};

let currentEncodedSession = '';
let expTimestamp = 0;
const updateState = (nextEncodedSession, store) => {
  if (currentEncodedSession !== nextEncodedSession) {
    currentEncodedSession = nextEncodedSession;
    if (currentEncodedSession) {
      localStorage.setItem(LOCAL_STORAGE_KEY, currentEncodedSession);
      const { user, idToken, exp } = decodeUser(currentEncodedSession);
      expTimestamp = exp;
      store.dispatch(updateUser(user, idToken));
      store.dispatch(checkUser());
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      expTimestamp = 0;
      store.dispatch(clearUser());
    }
    return true;
  }
  return false;
};


const setStorageFromOauthResponse = ({
  access_token: token, expires_in: ein, refresh_token: refreshToken, id_token: idToken,
}, nonceEnc, store) => {
  const { nonce } = jwtDecode(idToken);

  if (CHECK_NONCE && nonceEnc && !isValidNonce({ nonceEnc, nonceHash: nonce })) {
    throw new Error('Invalid nonce');
  }

  const tokenData = {
    token,
    exp: (ein * 1000) + Date.now(),
    refreshToken,
    idToken,
  };
  const encoded = base64url.encode(JSON.stringify(tokenData));
  updateState(encoded, store);
  return tokenData;
};


export const logout = (store) => {
  const stored = getStored();
  if (stored) {
    updateState('', store);
    if (REVOKE_ENDPOINT) {
      const tokenData = JSON.parse(base64url.decode(stored));
      const { refreshToken } = tokenData;
      if (refreshToken) {
        axios({
          method: 'post',
          url: resolveOauthPath(REVOKE_ENDPOINT),
          auth: {
            username: CLIENT_ID,
            password: '',
          },
          data: qs.stringify({
            token_type_hint: 'refresh_token',
            token: refreshToken,
          }),
          headers: {
            'content-type': 'application/x-www-form-urlencoded',
          },
        });
      }
    }
  }
  history.push('/view/loggedout');
};

const getCallbackUrl = () => {
  const url = window.location.href;
  const split = url.split('/');
  return `${split[0]}//${split[2]}${BASE_PATH}/view/callback`;
};


const getLoginUrl = () => resolveOauthPath(AUTHORIZE_ENDPOINT);

const redirectUri = getCallbackUrl();
const loginUrl = getLoginUrl();

const sha256 = buffer => crypto.createHash('sha256').update(buffer).digest();


export const redirectLogin = (options = {}) => {
  const { prompt, newWindow } = options;
  const stateKey = base64url.encode(crypto.randomBytes(32));
  const { nonceEnc, nonceHash } = generateNonce();
  const codeVerifier = base64url.encode(crypto.randomBytes(32));
  const state = {
    url: window.location.href,
    codeVerifier,
    expires: Date.now() + loginTimeout,
    nonce: nonceEnc,
    newWindow: !!newWindow,
  };
  localStorage.setItem(stateKey, base64url.encode(JSON.stringify(state)));
  const challenge = base64url.encode(sha256(codeVerifier));
  const params = {
    response_type: 'code',
    client_id: CLIENT_ID,
    scope,
    redirect_uri: redirectUri,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state: stateKey,
    nonce: nonceHash,
  };
  if (ACR_VALUES) {
    params.acr_values = ACR_VALUES;
  }
  if (prompt) {
    params.prompt = prompt;
  }
  const url = `${loginUrl}?${qs.stringify(params)}`;
  if (newWindow) {
    window.open(url, 'HazEL Login', 'status=no,location=no,toolbar=no,menubar=no,width=800,height=800,left=0,top=0');
  } else {
    window.location.replace(url);
  }
};

export const redirectLoginAndWait = (doLogin, encodedSession) => {
  if (doLogin) {
    redirectLogin({ newWindow: true });
  }
  const now = Date.now();
  const endTime = now + maxTime;
  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      const rightNow = Date.now();
      const nextEncodedSession = getStored();
      if (nextEncodedSession !== encodedSession) {
        clearInterval(interval);
        resolve(nextEncodedSession);
      } else if (rightNow > endTime) {
        clearInterval(interval);
        reject(new Error(`Login unsuccessful after ${maxTime} ms`));
      }
    }, pollInterval);
  });
};

// eslint-disable-next-line no-unused-vars
const refreshAccessToken = (stored, store) => {
  const tokenData = JSON.parse(base64url.decode(stored));
  const { refreshToken } = tokenData;
  if (refreshToken) {
    localStorage.setItem(LOCAL_STORAGE_REFRESH_KEY, `${true}`);
    return axios({
      method: 'post',
      url: resolveOauthPath(TOKEN_ENDPOINT),
      data: qs.stringify({
        client_id: CLIENT_ID,
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }),
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
      },
    }).then(
      ({ data }) => ({ refresh_token: refreshToken, ...data }),
      (error) => {
        const message = 'Authentication Failed';
        store.dispatch(addErrorMessage(message));
        updateState('', store);
        throw error;
      },
    );
  }
  localStorage.setItem(LOCAL_STORAGE_REFRESH_KEY, `${true}`);
  redirectLogin({ newWindow: true });
  updateState('', store);
  return Promise.reject(new Error('No refresh token'));

  // return promise;
};


const pollStorage = (store) => {
  const now = Date.now();
  const nextEncodedSession = getStored();
  const updated = updateState(nextEncodedSession, store);
  if (!updated && expTimestamp && expTimestamp - now <= 3 * pollInterval) {
    refreshAccessToken(nextEncodedSession, store)
      .then(response => setStorageFromOauthResponse(response, null, store));
  }
};

export const start = (store) => {
  pollStorage(store);
  return setInterval(() => { pollStorage(store); }, pollInterval);
};
export const exchangeCodeForToken = ({ state, code }, store) => {
  localStorage.setItem(LOCAL_STORAGE_REFRESH_KEY, `${false}`);
  if (!code || !state) {
    return Promise.reject(new Error('Invalid authentication request'));
  }
  const savedState = localStorage.getItem(state);
  if (!savedState) {
    const message = 'Invalid authentication request';
    store.dispatch(addErrorMessage(message));
    return Promise.reject(new Error(message));
  }
  localStorage.removeItem(state);
  const {
    url, codeVerifier, expires, nonce, newWindow,
  } = JSON.parse(base64url.decode(savedState));
  if (Date.now() > expires) {
    const message = 'login time exceeded';
    store.dispatch(addErrorMessage(message));
    return Promise.reject(message);
  }
  return axios({
    method: 'post',
    url: resolveOauthPath(TOKEN_ENDPOINT),
    data: qs.stringify({
      code,
      client_id: CLIENT_ID,
      grant_type: 'authorization_code',
      code_verifier: codeVerifier,
      redirect_uri: redirectUri,
      scope,
    }),
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
    },
  }).then(
    ({ data }) => ({ url, ...data }),
    (error) => {
      let errorData;
      const { response } = error;
      if (response) {
        const { data } = response;
        if (data) {
          errorData = data;
        }
      }
      const message = errorData ? JSON.stringify(errorData)
        : 'Authentication Failed';
      store.dispatch(addErrorMessage(message));
      history.push('/view');
      throw error;
    },
  ).then((response) => {
    setStorageFromOauthResponse(response, nonce, store);
    if (newWindow) {
      window.close();
    } else {
      window.location.replace(response.url);
    }
  }).catch((error) => {
    const message = 'Invalid authentication request';
    store.dispatch(addErrorMessage(message));
    throw error;
  });
};
